import React from 'react'

/**
 * Preset condition field values for CombinedRuleBuilder <select>s.
 * If a saved rule uses a field not listed here, UI still shows it via OrphanCombinedFieldOption.
 */

export const GAMING_API_FIELD_GROUPS = [
  {
    label: 'Gaming • EGM / meter row (PascalCase)',
    options: [
      { value: 'Turnover', label: 'Turnover' },
      { value: 'CreditWin', label: 'CreditWin' },
      { value: 'CancelCredit', label: 'CancelCredit' },
      { value: 'Stroke', label: 'Stroke' },
      { value: 'Credit', label: 'Credit' },
      { value: 'CashlessIn', label: 'CashlessIn' },
      { value: 'CashlessOut', label: 'CashlessOut' },
      { value: 'CashIn', label: 'CashIn' },
      { value: 'CashOut', label: 'CashOut' },
      { value: 'CashDrop', label: 'CashDrop' },
      { value: 'CoinOut', label: 'CoinOut' },
      { value: 'Bill5', label: 'Bill5' },
      { value: 'Bill10', label: 'Bill10' },
      { value: 'Bill20', label: 'Bill20' },
      { value: 'Bill50', label: 'Bill50' },
      { value: 'Bill100', label: 'Bill100' },
      { value: 'TicketsIn', label: 'TicketsIn' },
      { value: 'ExternalWin', label: 'ExternalWin' },
      { value: 'Rewards', label: 'Rewards' },
      { value: 'PointsToCr', label: 'PointsToCr' },
    ],
  },
  {
    label: 'Gaming • Identifiers & time',
    options: [
      { value: 'SessionPK', label: 'SessionPK' },
      { value: 'MemberID', label: 'MemberID' },
      { value: 'EgmPK', label: 'EgmPK' },
      { value: 'EgmName', label: 'EgmName' },
      { value: 'EgmHouseNo', label: 'EgmHouseNo' },
      { value: 'Venue', label: 'Venue' },
      { value: 'GameVariationPK', label: 'GameVariationPK' },
      { value: 'GamingDate', label: 'GamingDate' },
      { value: 'StartTime', label: 'StartTime' },
      { value: 'EndTime', label: 'EndTime' },
      { value: 'SnapshotTime', label: 'SnapshotTime' },
      { value: 'SnapShotTime', label: 'SnapShotTime' },
    ],
  },
  {
    label: 'Gaming • Combined interval (_alloc)',
    options: [
      { value: 'Turnover_alloc', label: 'Turnover_alloc' },
      { value: 'CreditWin_alloc', label: 'CreditWin_alloc' },
      { value: 'CancelCredit_alloc', label: 'CancelCredit_alloc' },
      { value: 'Stroke_alloc', label: 'Stroke_alloc' },
      { value: 'CashIn_alloc', label: 'CashIn_alloc' },
      { value: 'CashOut_alloc', label: 'CashOut_alloc' },
      { value: 'CashDrop_alloc', label: 'CashDrop_alloc' },
      { value: 'Bill5_alloc', label: 'Bill5_alloc' },
      { value: 'Bill10_alloc', label: 'Bill10_alloc' },
      { value: 'Bill20_alloc', label: 'Bill20_alloc' },
      { value: 'Bill50_alloc', label: 'Bill50_alloc' },
      { value: 'Bill100_alloc', label: 'Bill100_alloc' },
      { value: 'TicketsIn_alloc', label: 'TicketsIn_alloc' },
    ],
  },
  {
    label: 'TITO • Combined session (sessions.enriched)',
    options: [
      { value: 'sessions.enriched.tito_ticket_in_amount', label: 'Ticket in $ (redeemed into this session)' },
      { value: 'sessions.enriched.tito_ticket_in_count', label: 'Ticket in count' },
      { value: 'sessions.enriched.tito_ticket_out_amount', label: 'Ticket out $ (printed from this session)' },
      { value: 'sessions.enriched.tito_ticket_out_count', label: 'Ticket out count' },
      { value: 'sessions.enriched.tito_egm_hop_count', label: 'EGM-to-EGM hop count' },
      { value: 'sessions.enriched.tito_open_count', label: 'Open ticket count (printed, not redeemed)' },
    ],
  },
  {
    label: 'Cumulative • Member rollup (aggregation.*)',
    options: [
      { value: 'totalCashDrop', label: 'totalCashDrop (SUM cash_drop, windowed)' },
      { value: 'totalTurnover', label: 'totalTurnover (SUM turnover, windowed)' },
      { value: 'totalCashIn', label: 'totalCashIn (SUM cash_in, windowed)' },
      { value: 'totalCashOut', label: 'totalCashOut (SUM cash_out, windowed)' },
      { value: 'transactionCount', label: 'transactionCount (rollup)' },
      { value: 'maxSingleTransaction', label: 'maxSingleTransaction (rollup)' },
      { value: 'uniqueEGMs', label: 'uniqueEGMs (rollup)' },
      { value: 'uniqueVenues', label: 'uniqueVenues (rollup)' },
      { value: 'totalPayout', label: 'totalPayout (desk payouts in window)' },
      { value: 'totalPayoutUnlinked', label: 'totalPayoutUnlinked (no floor link accepted)' },
      { value: 'totalPayoutLinked', label: 'totalPayoutLinked (floor link accepted)' },
      { value: 'payoutCount', label: 'payoutCount (desk payout rows)' },
      { value: 'maxSinglePayout', label: 'maxSinglePayout (largest desk payout)' },
      { value: 'totalAmount', label: 'totalAmount (floor cash_in + desk payouts)' },
    ],
  },
]

export const GAMING_API_FIELD_VALUE_SET = new Set(
  GAMING_API_FIELD_GROUPS.flatMap((g) => g.options.map((o) => o.value))
)

/** Other option values from CombinedRuleBuilder simple-condition dropdown (non–gaming-api groups). */
const OTHER_COMBINED_PRESET_FIELD_VALUES = [
  'gaming.sessions.Turnover',
  'gaming.sessions.CreditWin',
  'meters.readings.CashIn',
  'sessions.enriched.turnover_currency',
  'sessions.enriched.cash_in_sum',
  'sessions.enriched.tito_ticket_in_amount',
  'sessions.enriched.tito_ticket_in_count',
  'sessions.enriched.tito_ticket_out_amount',
  'sessions.enriched.tito_ticket_out_count',
  'sessions.enriched.tito_egm_hop_count',
  'sessions.enriched.tito_open_count',
  'sessions.statsDaily.avg_turnover',
  'sessions.statsDaily.session_count',
  'sessions.tm01.changes.avg_turnover_change_pct',
  'NetAmount',
  'TransactionAmount',
  'CumulativeAmount',
  'TransactionType',
  'EventType',
  'PaymentMethod',
  'Currency',
  'TransactionStatus',
  'TransactionDate',
  'TransactionTime',
  'AccountID',
  'MemberTier',
  'MemberStatus',
  'VenueID',
  'TerminalID',
  'DeviceType',
  'IPAddress',
  'RiskScore',
  'AMLFlag',
  'SuspiciousFlag',
  'ComplianceStatus',
  'KycStatus',
  'SessionDuration',
  'TotalSpins',
  'AvgBetAmount',
  'MaxBetAmount',
  'TotalWagered',
  'TotalWon',
  'NetWinLoss',
  'GameID',
  'GameType',
  'GameCategory',
  'GameRTP',
  'GameVariance',
  'EGMID',
  'GMID',
  'DeviceLocation',
  'ResponsibleGamingStatus',
  'SessionStartTime',
  'SessionEndTime',
  'DayOfWeek',
  'TimeOfDay',
  'IsWeekend',
  'IsHoliday',
  'DeviceChanges',
  'GameChanges',
  'SessionsToday',
  'SessionsThisWeek',
  'AvgSessionDuration',
  'RapidPlayFlag',
  'ExtendedPlayFlag',
  'ResponsibleGamingTrigger',
  'AnomalyScore',
  'patron_risk.overall_score',
  'patron_risk.risk_level',
  'patron_risk.kyc_score',
  'patron_risk.aml_score',
  'patron_risk.gaming_score',
  'patron_risk.occupation_score',
  'patron_risk.case_score',
  'patron_risk.smr_score',
  'patron_risk.ecdd_score',
  'patron_risk.has_pep_match',
  'patron_risk.has_sanctions_match',
  'patron_risk.has_active_case',
  'patron_risk.tier_velocity_suspicious',
  'patron_risk.occupation_high_risk',
  'patron_risk.gaming_exceeds_income',
  'periodComparison.current.sessionCount',
  'periodComparison.previous.sessionCount',
  'periodComparison.current.avgTurnover',
  'periodComparison.previous.avgTurnover',
  'periodComparison.changes.turnoverChange',
  'periodComparison.changes.winPercentageChange',
]

/** Full preset set for “is this field already in the dropdown?” */
export const COMBINED_CONDITION_FIELD_CATALOG = new Set([
  ...GAMING_API_FIELD_VALUE_SET,
  ...OTHER_COMBINED_PRESET_FIELD_VALUES,
])

export function GamingApiFieldOptgroups() {
  return (
    <>
      {GAMING_API_FIELD_GROUPS.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  )
}

/**
 * Renders a single <option> when the saved rule field is not in the preset catalog
 * (so controlled <select> shows the correct value).
 */
export function OrphanCombinedFieldOption({ field }) {
  const f = field != null ? String(field).trim() : ''
  if (!f || COMBINED_CONDITION_FIELD_CATALOG.has(f)) return null
  return <option value={f}>{f}</option>
}
